import React, { useState, useCallback, useMemo } from 'react';
import { Box } from '@atlaskit/primitives/compiled';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { EditableAvatar } from '@atlassian/jira-navigation-apps-sidebar-inline-config-project-header/src/ui/editable-avatar/index.tsx';
import { JiraPopup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import messages from '../messages.tsx';
import { navigationKitAvatarPopupContentEntrypoint } from './entrypoint.tsx';

type NavigationKitAvatarPopupProps = {
	canEdit: boolean;
	projectId: number;
	selectedAvatarUrl: string;
};

export const NavigationKitAvatarPopup = ({
	canEdit,
	projectId,
	selectedAvatarUrl,
}: NavigationKitAvatarPopupProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();
	const onAvatarClick = useCallback(() => {
		setIsOpen(true);
	}, [setIsOpen]);
	const onClose = useCallback(() => {
		setIsOpen(false);
	}, [setIsOpen]);
	const selectedAvatarId = Number(
		selectedAvatarUrl.substring(selectedAvatarUrl.lastIndexOf('/') + 1),
	);
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		navigationKitAvatarPopupContentEntrypoint,
		{},
	);
	const entrypointTrigger = useEntryPointButtonTrigger(entryPointActions);

	const runtimeProps = useMemo(
		() => ({ onClose, projectId, selectedAvatarId, selectedAvatarUrl }),
		[onClose, projectId, selectedAvatarId, selectedAvatarUrl],
	);

	return (
		<JiraPopup
			messageId="horizontal-nav.ui.content.header.project-header.project-avatar.navigation-kit-avatar-popup.jira-popup.popup"
			messageType="transactional"
			placement="bottom-start"
			role="dialog"
			label={formatMessage(messages.title)}
			isOpen={isOpen}
			onClose={onClose}
			content={() => (
				<JiraEntryPointContainer
					id="navigation-kit-avatar.popup-content"
					packageName="jiraNavigationKitAvatarPopupContent"
					entryPointReferenceSubject={entryPointReferenceSubject}
					runtimeProps={runtimeProps}
				/>
			)}
			trigger={(triggerProps) => {
				return (
					<Box ref={entrypointTrigger}>
						<EditableAvatar
							avatarUrl={selectedAvatarUrl}
							canEdit={canEdit}
							onClick={onAvatarClick}
							triggerProps={triggerProps}
						/>
					</Box>
				);
			}}
		/>
	);
};
